import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DjiFlight } from '../../../core/data-models/dji-flight.model';
import { DroneFlightEntry } from '../../../core/data-models/drone-flight-entry.model';
import {
  AcreageComparison,
  DjiFlightTotals,
  averageSpeedMps,
  compareAcreage,
  djiDetailStatusLabel,
  formatDjiAcres,
  formatDjiApplied,
  formatDjiAppliedTotal,
  formatDjiDifference,
  formatDjiDuration,
  formatDjiFlightStart,
  formatDjiSpeed,
  summarizeDjiFlights
} from '../../utils/dji-flight.utils';

/**
 * What DJI recorded for ONE logged drone flight, set beside what the pilot
 * logged for it (ADN-2307). A logged drone flight is one aircraft's work on a
 * spray record; the DJI flights matched to that aircraft are its individual
 * takeoff-and-landing flights, so they are totalled and compared, and each is
 * listed underneath.
 *
 * Renders nothing when DJI has no flight for this aircraft, so a drone flight
 * without DJI data looks exactly as it did before.
 */
@Component({
  selector: 'app-dji-flight-comparison',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dji-flight-comparison.component.html'
})
export class DjiFlightComparisonComponent {
  /** The pilot's logged entry for this aircraft. */
  @Input({ required: true }) logged!: Pick<DroneFlightEntry, 'acreageSprayed' | 'flightSpeed' | 'numFlights'>;
  /** DJI flights matched to this same aircraft. */
  @Input() djiFlights: DjiFlight[] = [];
  /** Asks the page to reload the DJI flights, e.g. while details are still processing. */
  @Output() refresh = new EventEmitter<void>();

  readonly acres = formatDjiAcres;
  readonly applied = formatDjiApplied;
  readonly duration = formatDjiDuration;
  readonly speed = formatDjiSpeed;
  readonly startLabel = formatDjiFlightStart;
  readonly differenceLabel = formatDjiDifference;
  readonly statusLabel = djiDetailStatusLabel;

  get totals(): DjiFlightTotals {
    return summarizeDjiFlights(this.djiFlights);
  }

  /** DJI's covered area against the acreage the pilot logged for this aircraft. */
  get acreageComparison(): AcreageComparison | null {
    return compareAcreage(this.logged.acreageSprayed, this.totals.totalAcres);
  }

  get appliedTotal(): string {
    return formatDjiAppliedTotal(this.totals);
  }

  get averageSpeed(): string {
    return formatDjiSpeed(averageSpeedMps(this.djiFlights));
  }

  get anyProcessing(): boolean {
    return this.djiFlights.some((f) => f.detailStatus === 'processing');
  }

  trackByIntake(_index: number, flight: DjiFlight): string {
    return flight.intakeId;
  }
}
